/**
 * PipelinePickerDialog — Lets the user select a pipeline from another batch
 * within the same project for cross-batch pipeline reuse.
 *
 * Shows a list of pipelines with batch name, date, step count, and source tables.
 * User selects one and clicks "Use This Pipeline" to proceed to source mapping.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  alpha,
} from '@mui/material';
import { AccountTree as PipelineIcon } from '@mui/icons-material';
import { listProjectPipelines, type ProjectPipeline } from '../../services/workspaceApi';

interface PipelinePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (pipelineId: string) => void;
  projectId: string;
  /** Exclude pipelines from this batch (the current one). */
  excludeBatchId?: string;
}

const PipelinePickerDialog: React.FC<PipelinePickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  projectId,
  excludeBatchId,
}) => {
  const [pipelines, setPipelines] = useState<ProjectPipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setSelected(null);
    setError(null);
    setLoading(true);

    listProjectPipelines(projectId)
      .then((data) => {
        const filtered = excludeBatchId
          ? data.filter((p) => p.batch_id !== excludeBatchId)
          : data;
        setPipelines(filtered);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err.message || 'Failed to load pipelines');
      })
      .finally(() => setLoading(false));
  }, [open, projectId, excludeBatchId]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reuse Pipeline from Another Batch</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a pipeline to apply to this batch. Source table mappings will be confirmed in the next step.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        )}

        {!loading && !error && pipelines.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No pipelines found in this project.
          </Typography>
        )}

        {!loading && pipelines.length > 0 && (
          <List sx={{ mx: -1 }}>
            {pipelines.map((p) => (
              <ListItemButton
                key={p.pipeline_id}
                selected={selected === p.pipeline_id}
                onClick={() => setSelected(p.pipeline_id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  border: selected === p.pipeline_id
                    ? '1.5px solid #2563eb'
                    : '1.5px solid transparent',
                  '&.Mui-selected': {
                    bgcolor: alpha('#2563eb', 0.06),
                  },
                }}
              >
                <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                  <PipelineIcon sx={{ fontSize: 20, color: '#64748b' }} />
                </Box>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600 }}>
                      {p.batch_name || `Batch ${p.batch_id.slice(0, 8)}`}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                      <Typography component="span" variant="caption" sx={{ color: '#64748b' }}>
                        {p.step_count} step{p.step_count !== 1 ? 's' : ''} · {formatDate(p.created_at)}
                      </Typography>
                      {p.source_tables.length > 0 && (
                        <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {p.source_tables.map((t) => (
                            <Chip
                              key={t}
                              label={t}
                              size="small"
                              sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  }
                />
                <Chip
                  label={`v${p.version}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 10, height: 20, ml: 1 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => selected && onSelect(selected)}
          disabled={!selected}
          variant="contained"
        >
          Use This Pipeline
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PipelinePickerDialog;
